import {
  Box,
  ExpandMoreRoundedIcon,
  Flex,
  IconButton,
  Rotate,
  styled,
  TableCell,
  TableRow,
  Text,
  theme,
  type TableRowProps,
} from '@shared/components';

export interface LOSDashboardTableRowProps extends TableRowProps {
  data: {
    id: string;
    levelName: string | undefined;
    parentId: string | null;
    parentIds: string[];
    calculate?: {
      hardQuestions: number | null;
      mediumQuestions: number | null;
      easyQuestions: number | null;
    };
    nonCalculate?: {
      hardQuestions: number | null;
      mediumQuestions: number | null;
      easyQuestions: number | null;
    };
    total?: string;
  };
  indent?: number;
  expanded?: boolean;
  onExpand?: (id: string) => void;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[300],
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  ...theme.typography.body2Semibold,
}));

const LOS_LEVEL_BG_COLOR = `hsl(42, 100%, 96%)`;

function renderIndent(indent: number) {
  return (
    <>
      {Array.from({ length: indent }).map((_, index) => (
        <Box
          key={index}
          sx={{
            minWidth: '16px',
            borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`,
          }}
        />
      ))}
    </>
  );
}

export default function LOSDashboardTableRow(props: Readonly<LOSDashboardTableRowProps>) {
  const { data, indent = 0, expanded: isExpanded = false, onExpand, ...restProps } = props;
  const { id, levelName, calculate, nonCalculate, total } = data;

  return (
    <TableRow
      {...restProps}
      sx={{
        border: 'none',
        ':last-child': {
          borderRight: 'none',
          td: {
            borderBottom: 'none',
          },
        },
        backgroundColor: isExpanded ? LOS_LEVEL_BG_COLOR : theme.palette.background.paper,
        ...restProps.sx,
      }}
    >
      <StyledTableCell
        sx={{
          p: (theme) => theme.spacing(0),
          borderBottom: 'none',
        }}
      >
        <Flex
          sx={{
            minHeight: '48px',
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          {renderIndent(indent)}
          <Flex
            sx={{
              alignItems: 'center',
              backgroundColor: isExpanded ? LOS_LEVEL_BG_COLOR : theme.palette.background.paper,
              flex: 1,
              borderBottom: '1px solid ' + theme.palette.grey[300],
            }}
          >
            <Flex sx={{ minWidth: '48px', alignItems: 'center', justifyContent: 'center' }}>
              {onExpand ? (
                <IconButton onClick={() => onExpand(id)}>
                  <Rotate in={isExpanded}>
                    <ExpandMoreRoundedIcon />
                  </Rotate>
                </IconButton>
              ) : null}
            </Flex>
            <Flex
              sx={(theme) => ({
                flex: 1,
                p: theme.spacing(2, 3),
                gap: theme.spacing(2),
              })}
            >
              <Text>{levelName}</Text>
            </Flex>
          </Flex>
        </Flex>
      </StyledTableCell>
      <StyledTableCell align='center'>{calculate?.easyQuestions ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{calculate?.mediumQuestions ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{calculate?.hardQuestions ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{nonCalculate?.easyQuestions ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{nonCalculate?.mediumQuestions ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{nonCalculate?.hardQuestions ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{total ?? '-'}</StyledTableCell>
    </TableRow>
  );
}
